"use client";

/**
 * Fork choice with two selectable cards + checkmark.
 *
 * Extracted from `compass-intake.jsx:364-380` (`.cs-fork`, `.cs-opt`, `.cs-optck` classes).
 * Used at the entry point of the intake flow to choose between two approaches.
 */

import { Check } from "../lib/icons";

export interface TwoOptionSelectOption {
  /** Option heading. */
  title: string;
  /** Short description below the heading. */
  description: string;
}

export interface TwoOptionSelectProps {
  /** Exactly two options to display. */
  options: TwoOptionSelectOption[];
  /** Index of the currently selected option (0 or 1), or undefined if none. */
  selected?: number;
  /** Called when an option is clicked. */
  onSelect?: (index: number) => void;
}

/**
 * Two-card fork selector with a checkmark on the selected card.
 *
 * `.cs-fork` → flex col, gap-3
 * `.cs-opt`  → flex row, border, rounded-container, hover highlight
 * `.cs-optck` → circular check badge
 */
export function TwoOptionSelect({
  options,
  selected,
  onSelect,
}: TwoOptionSelectProps) {
  return (
    <div className="flex flex-col gap-3">
      {options.map((opt, i) => {
        const isOn = selected === i;
        return (
          <button
            key={i}
            type="button"
            className={[
              "flex gap-[14px] items-start text-left",
              "bg-(--bg-white) border rounded-(--radius-container)",
              "p-[18px] cursor-pointer",
              "transition-all duration-300",
              "font-[family-name:var(--font-body)]",
              isOn
                ? "border-(--fg-brand) shadow-[inset_0_0_0_1px_var(--fg-brand)]"
                : "border-(--border-default) hover:border-(--fg-brand) hover:bg-(--bg-sky)",
            ].join(" ")}
            onClick={() => onSelect?.(i)}
          >
            {/* Icon area */}
            <span
              className={[
                "flex-shrink-0 w-[44px] h-[44px]",
                "rounded-(--radius-sm)",
                "flex items-center justify-center",
                "transition-colors duration-300",
                isOn
                  ? "bg-(--brand-navy) text-(--accent-lime)"
                  : "bg-(--bg-sky) text-(--fg-brand)",
              ].join(" ")}
            >
              <span className="text-[20px] font-bold">{i + 1}</span>
            </span>

            {/* Text */}
            <span className="flex flex-col gap-1 flex-1 min-w-0">
              <span className="text-[15px] font-semibold text-(--fg-primary)">
                {opt.title}
              </span>
              <span className="text-[12.5px] leading-[1.5] text-(--fg-secondary)">
                {opt.description}
              </span>
            </span>

            {/* Checkmark */}
            <span
              className={[
                "flex-shrink-0 w-[22px] h-[22px]",
                "rounded-full border-[1.5px]",
                "flex items-center justify-center",
                "mt-[11px]",
                isOn
                  ? "bg-(--brand-navy) border-(--brand-navy) text-white"
                  : "border-(--border-strong) text-transparent",
              ].join(" ")}
            >
              <Check size={12} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
